import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePostHog } from 'posthog-js/react'

import { useBreadcrumbs } from '../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'

import styles from './Dashboard.module.scss'
import { POSTHOG_FEATURE_FLAGS, useAuthState, usePostHogFeatureFlag } from '@percent/partner-dashboard/common/hooks'
import { useAreFeatureFlagsReady } from '@percent/partner-dashboard/app/providers/PostHogProvider/PostHogProvider'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { selectScopes } from '@percent/partner-dashboard/context/auth/authReducer/authReducer'
import { PartnerScopes } from '@percent/partner-dashboard/constants/scopes'
import { Heading, Text } from '@percent/lemonade'
import { DonationInsights } from './DonationInsights'
import { InsightsAiQuestionBox } from './InsightsAiQuestionBox/InsightsAiQuestionBox'
import bannerHeartImg from '@percent/partner-dashboard/common/assets/images/banner-heart.png'
import bannerCoinImg from '@percent/partner-dashboard/common/assets/images/banner-coin.png'

export function Dashboard() {
  const { user, partner } = useAuthState()
  const { t } = useTranslation()
  const donationSummaryChartsFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_DONATION_SUMMARY_CHARTS)
  const insightsAiQuestionBoxFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_INSIGHTS_AI_CHAT)
  const areFeatureFlagsReady = useAreFeatureFlagsReady()
  const { userCan, arePermissionsResolved } = usePermissionCheck()
  const posthog = usePostHog()
  const scopes = selectScopes(partner?.scopes)
  const hasPartnerInsightsScope = Boolean(scopes[PartnerScopes.PartnerInsights])
  const canViewDonationInsights =
    donationSummaryChartsFeatureFlag && hasPartnerInsightsScope && userCan('viewPartnerInsights')
  const canViewInsightsAiQuestionBox =
    insightsAiQuestionBoxFeatureFlag && hasPartnerInsightsScope && userCan('viewPartnerInsights')

  // The feature flag and permissions both resolve asynchronously after mount,
  // and the flag can briefly report a stale (anonymous) value before PostHog
  // re-evaluates it for the identified user. Until then `canViewDonationInsights`
  // is false, which would flash the welcome banner in before the insights
  // section replaces it on first visit. Only treat the gate as resolved once we
  // can decide the outcome definitively: flags are ready for the current
  // identity and either insights is already ruled out (flag off or scope
  // missing) or the permissions fetch has settled (on error this falls back to
  // showing the banner).
  // Both insights features share this gate: either one rendering replaces the banner, so the
  // banner must wait for whichever could still turn on. Checking only the donation-charts flag
  // would resolve the gate early when that flag is off but the AI box's is on — flashing the
  // banner in until permissions settle and `canViewInsightsAiQuestionBox` flips true.
  const isDonationInsightsGateResolved =
    areFeatureFlagsReady &&
    ((!donationSummaryChartsFeatureFlag && !insightsAiQuestionBoxFeatureFlag) ||
      !hasPartnerInsightsScope ||
      arePermissionsResolved)
  useBreadcrumbs([
    {
      label: t('breadcrumb.dashboard')
    }
  ])

  // Surface per-feature eligibility as PostHog person properties so each
  // survey can be targeted at the right audience in the PostHog dashboard
  // (e.g. an insights NPS survey targets `canViewDonationInsights = true`).
  // Keeping the rule in PostHog rather than code lets survey owners launch
  // new feature-scoped surveys without shipping app changes.
  useEffect(() => {
    if (!posthog) return
    posthog.setPersonProperties({
      canViewDonationInsights: Boolean(canViewDonationInsights)
    })
  }, [posthog, canViewDonationInsights])

  return (
    <div className={styles.dashboardContainer} data-testid="dashboard-container">
      <div className={styles.contentGreetingWrapper} data-testid="greeting-section">
        <div className={styles.icon}>
          <span className={styles.wave} role="img" aria-label="Waving hand">
            👋
          </span>
        </div>
        <div className={styles.greetingContainer} data-testid="greeting-text">
          <Heading level="h2" align="center">
            {`${t('typography.dashboard.title')}${
              user?.preferredName || user?.fullName ? `, ${user?.preferredName || user?.fullName}` : ''
            }`}
          </Heading>
        </div>
      </div>
      {isDonationInsightsGateResolved && !canViewDonationInsights && !canViewInsightsAiQuestionBox && (
        <div className={styles.contentBannerWrapper} data-testid="banner-section">
          <div className={styles.leftColumn}>
            <img src={bannerHeartImg} alt="heart" className={styles.bannerHeart} />
          </div>
          <div className={styles.bannerText} data-testid="banner-text">
            <Heading level="h1" align="center">
              {t('typography.dashboard.bannerTitle')}
            </Heading>
            <Text size="large">{t('typography.dashboard.bannerSubtitle', { partnerName: partner?.name })}</Text>
          </div>
          <div className={styles.rightColumn}>
            <img src={bannerCoinImg} alt="coin" className={styles.bannerCoin} />
          </div>
        </div>
      )}
      {canViewInsightsAiQuestionBox && <InsightsAiQuestionBox />}
      {canViewDonationInsights && <DonationInsights />}
    </div>
  )
}
